import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import siteSetting from '../../config/env';
import './PolicyPage.css';

/**
 * Public policy page — Partner Terms / Client Terms / Privacy Policy.
 *
 * Renders the current published version from the v2 policy API (versioned,
 * admin-managed). All four types have published versions; the old fallback
 * to the legacy v1 CMS is gone, so a failed fetch shows "not available yet".
 */

const DEFAULT_TITLES = {
  partner_terms: 'Partner Terms of Business',
  beu_pay_terms: 'Beu Pay Terms',
  client_terms: 'Terms and Conditions',
  privacy_policy: 'Privacy Policy',
};

const PolicyPage = ({ type }) => {
  const [policy, setPolicy] = useState(null);      // v2 doc {title, content, version, effectiveDate}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPolicy(null);

    axios.get(`${siteSetting.api.v2}policy/${type}`)
      .then((res) => {
        if (cancelled) return;
        const doc = res?.data?.responseData;
        if (doc && doc.content) {
          setPolicy(doc);
          setLoading(false);
        } else {
          throw new Error('no published version');
        }
      })
      .catch(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [type]);

  const title = policy?.title || DEFAULT_TITLES[type];

  return (
    <div className="policy-page">
      <header className="policy-page__header">
        <div className="policy-page__header-inner">
          <h1 className="policy-page__title">{title}</h1>
          {policy && policy.effectiveDate && (
            <p className="policy-page__meta">
              Version {policy.version} · effective {new Date(policy.effectiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>
      </header>

      <div className="policy-page__body">
        <div className="policy-page__body-inner">
          {loading && (
            <div className="policy-skeleton" aria-hidden="true">
              {[80, 95, 70, 90, 60, 88, 50].map((w, i) => (
                <div key={i} className={`policy-skeleton__line${w < 65 ? ' short' : ''}`} style={{ width: `${w}%` }} />
              ))}
            </div>
          )}
          {!loading && policy && (
            <div className="policy-content"><ReactMarkdown>{policy.content}</ReactMarkdown></div>
          )}
          {!loading && !policy && (
            <p className="policy-state">This document isn’t available yet. Please contact info@beuapp.com.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const PartnerTermsPage = () => <PolicyPage type="partner_terms" />;
export const BeuPayTermsPage = () => <PolicyPage type="beu_pay_terms" />;
export const ClientTermsPage = () => <PolicyPage type="client_terms" />;
export const PrivacyPolicyPage = () => <PolicyPage type="privacy_policy" />;

export default PolicyPage;
